import { Button, Checkbox } from '@otter-ai/den-components';
import type { GetWorkspaceResponse } from '@otter-ai/recall-client';
import { useAtom } from 'jotai';
import { useTranslations } from 'next-intl';
import { MEMBER_STATUS } from '@/features/manage-team/team-profile/TeamMembersTable';
import type { TeamMembersActions } from '@/features/manage-team/team-profile/useTeamMembersActions';
import { selectedMemberIdsAtom } from './atoms';

type Member = GetWorkspaceResponse['workspace']['members'][number];

export function MembersTableHeader({
  memberActions,
  members,
  canHideMembers,
}: {
  memberActions: TeamMembersActions;
  members: Member[];
  canHideMembers: boolean;
}) {
  const t = useTranslations('manage-team:team-profile');
  const [selectedIds, setSelectedIds] = useAtom(selectedMemberIdsAtom);

  const selectedMembers = members.filter((m) => selectedIds.has(Number(m.id)));
  const selectedEmails = selectedMembers.map((m) => m.member_email);
  const selectedRelationshipIds = selectedMembers.map((m) => Number(m.id));
  const selectedUserIds = selectedMembers.map((m) => m.member_id ?? 0);

  const areAllMembersInvited = selectedMembers.every(isInvited);

  const shouldShowHideBtn = canHideMembers && selectedMembers.some(isHideable);
  const shouldShowUnhideBtn =
    canHideMembers && selectedMembers.some(isUnhideable);
  const shouldShowRevokeAccessBtn =
    selectedMembers.every(isRevocable) && !areAllMembersInvited;
  const shouldShowActivateBtn =
    selectedMembers.every(isDeactivated) && !areAllMembersInvited;
  const shouldShowDeleteBtn = shouldShowActivateBtn && !areAllMembersInvited;

  if (selectedIds.size === 0) {
    return null;
  }
  return (
    <div className="flex items-center gap-3 rounded-lg bg-surface-secondary px-3 py-1">
      <Checkbox
        checked={
          selectedIds.size === members.length
            ? true
            : selectedIds.size > 0
              ? 'indeterminate'
              : false
        }
        onCheckedChange={(checked) => {
          if (checked) {
            setSelectedIds(new Set(members.map((m) => Number(m.id))));
          } else {
            setSelectedIds(new Set());
          }
        }}
        aria-label={t('bulkActions.selectAll')}
      />
      <span className="text-sm font-medium text-subtle">
        {t('bulkActions.selected', { count: selectedIds.size })}
      </span>
      <div className="flex gap-2">
        {shouldShowHideBtn && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() =>
              memberActions.bulkHide({
                selectedIds,
                relationshipIds: selectedRelationshipIds,
                userIds: selectedUserIds,
                emails: selectedEmails,
              })
            }
          >
            {t('bulkActions.hide')}
          </Button>
        )}
        {shouldShowUnhideBtn && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() =>
              memberActions.bulkUnhide({
                selectedIds,
                relationshipIds: selectedRelationshipIds,
                userIds: selectedUserIds,
                emails: selectedEmails,
              })
            }
          >
            {t('bulkActions.unhide')}
          </Button>
        )}
        {shouldShowRevokeAccessBtn && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() =>
              memberActions.bulkRevokeAccess({
                selectedIds,
                relationshipIds: selectedRelationshipIds,
                userIds: selectedUserIds,
                emails: selectedEmails,
              })
            }
          >
            {t('bulkActions.revokeAccess')}
          </Button>
        )}
        {shouldShowActivateBtn && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() =>
              memberActions.bulkActivate({
                selectedIds,
                relationshipIds: selectedRelationshipIds,
                userIds: selectedUserIds,
                emails: selectedEmails,
              })
            }
          >
            {t('bulkActions.activate')}
          </Button>
        )}
        {shouldShowDeleteBtn && (
          <Button
            size="sm"
            variant="negative"
            onClick={() =>
              memberActions.bulkDelete({
                selectedIds,
                relationshipIds: selectedRelationshipIds,
                userIds: selectedUserIds,
                emails: selectedEmails,
              })
            }
          >
            {t('bulkActions.delete')}
          </Button>
        )}
      </div>
    </div>
  );
}

const isHideable = (m: Member) => !m.is_hidden && !m.deactivated_at;
const isUnhideable = (m: Member) => m.is_hidden && !m.deactivated_at;
const isRevocable = (m: Member) =>
  !m.is_admin && !m.is_pending && !m.deactivated_at;
const isDeactivated = (m: Member) =>
  !m.is_admin && !m.is_pending && Boolean(m.deactivated_at);
const isInvited = (m: Member) => m.member_status === MEMBER_STATUS.INVITED;
